import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  CalculatorIcon, BanknotesIcon, ExclamationTriangleIcon, XMarkIcon, ListBulletIcon, ChevronDownIcon,
} from '@heroicons/react/24/outline';
import tariffService from '../../services/tariffService';
import patientService from '../../services/patientService';
import { errText } from '../../services/scheduleService';
import TariffLines, { rands } from '../../components/billing/TariffLines';
import TariffPicker from '../../components/billing/TariffPicker';
import SchemeRates from './SchemeRates';

const SOURCE = {
  placeholder: { label: 'Placeholder — not SAMA', tone: 'bg-amber-100 text-amber-800' },
  public_scheme_file: { label: 'Public scheme file — dev only', tone: 'bg-amber-100 text-amber-800' },
  licensed: { label: 'Licensed', tone: 'bg-green-100 text-green-700' },
};
const PROVENANCE = {
  invented: { label: 'Invented', tone: 'bg-slate-100 text-slate-600' },
  recalled_unverified: { label: 'Unverified', tone: 'bg-amber-50 text-amber-700' },
  licensed: { label: 'Licensed', tone: 'bg-green-50 text-green-700' },
};
const TYPE_LABEL = { surgical: 'Surgical', anaesthetic_procedure: 'Anaesthetic procedures', consultation: 'Consultations' };

const Badge = ({ map, value }) => {
  const b = map[value];
  return b ? <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${b.tone}`}>{b.label}</span> : null;
};

const ruleText = (rule) => {
  const e = rule.effect;
  const effect = !e ? '' : e.kind === 'add_units' ? `+${e.units} units` : `+${e.percent}% of ${e.of === 'base_and_time' ? 'base + time' : 'base'} units`;
  switch (rule.trigger) {
    case 'time': return 'Units from the anaesthetic time';
    case 'fact': return `When ${rule.fact === 'emergency' ? 'it is an emergency' : rule.fact === 'prone' ? 'the patient is prone' : rule.fact}: ${effect}`;
    case 'age_under': return `Patient under ${rule.years} year${rule.years === 1 ? '' : 's'}: ${effect}`;
    case 'age_at_least': return `Patient ${rule.years}+: ${effect}`;
    case 'bmi_at_least': return `BMI ${rule.value}+: ${effect}`;
    default: return rule.trigger;
  }
};

/**
 * TariffPanel — the tariff half of Codes & Tariffs: the active release (clearly
 * flagged while it is the placeholder), the practice's fee schedules, a fee
 * calculator that runs the same engine as the preview invoice, and the code list.
 */
const TariffPanel = () => {
  const [status, setStatus] = useState(null);
  const [schedules, setSchedules] = useState([]);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    try {
      const [s, f] = await Promise.all([tariffService.status(), tariffService.schedules()]);
      setStatus(s);
      setSchedules(f);
      setError('');
    } catch (e) {
      setError(errText(e));
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const release = status?.release;
  const byType = useMemo(() => {
    const out = {};
    (status?.counts || []).forEach((c) => { out[c.item_type] = (out[c.item_type] || 0) + c.n; });
    return out;
  }, [status]);

  return (
    <div className="mt-10">
      <div className="mb-4">
        <h2 className="text-xl font-bold text-brand-deep">Tariffs</h2>
        <p className="mt-1 text-sm text-slate-500">Procedure codes, units and modifiers turn a coded case into a fee. What a unit is worth is set per practice.</p>
      </div>

      {error && <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>}

      {release && release.source_kind !== 'licensed' && (
        <div className="mb-4 flex gap-3 rounded-xl border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          <ExclamationTriangleIcon className="mt-0.5 h-5 w-5 shrink-0" />
          <div>
            <p className="font-semibold">These tariff codes are a placeholder, not SAMA’s coding data.</p>
            <p className="mt-0.5 text-amber-800">
              Procedure codes (9xxx) and most values are invented; modifiers 0023, 0011 and 0018 are unverified. They let billing be built and tested
              until the licensed file arrives — never claim with them. Details: <code className="rounded bg-white/70 px-1">backend/src/tariffs/PLACEHOLDER.md</code>.
            </p>
          </div>
        </div>
      )}

      <div className="mb-6 grid gap-4 lg:grid-cols-2">
        {/* Release */}
        <section className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
          <div className="flex items-start gap-4">
            <span className="grid h-11 w-11 shrink-0 place-items-center rounded-xl bg-brand-gradient text-white"><ListBulletIcon className="h-6 w-6" /></span>
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2">
                <h3 className="font-bold text-slate-800">Tariff code release</h3>
                {release && <Badge map={SOURCE} value={release.source_kind} />}
              </div>
              {status === null ? <p className="text-sm text-slate-500">Loading…</p>
                : release ? <p className="text-sm text-slate-500">{release.label}</p>
                  : <p className="text-sm text-slate-500">No tariff codes loaded. Run <code>db/seedTariffPlaceholder.js</code>.</p>}
            </div>
          </div>
          {release && (
            <>
              <dl className="mt-4 grid grid-cols-3 gap-2 text-sm">
                {Object.entries(TYPE_LABEL).map(([k, label]) => (
                  <div key={k} className="rounded-lg bg-slate-50 px-3 py-2">
                    <dt className="text-[11px] text-slate-500">{label}</dt>
                    <dd className="text-lg font-bold text-slate-800">{byType[k] || 0}</dd>
                  </div>
                ))}
              </dl>
              <h4 className="mt-4 text-xs font-semibold uppercase tracking-wide text-slate-500">Modifiers</h4>
              <ul className="mt-1 divide-y divide-slate-100 text-sm">
                {status.modifiers.map((m) => (
                  <li key={m.code} className="flex items-start gap-3 py-1.5">
                    <span className="w-12 shrink-0 font-mono font-semibold text-brand-navy">{m.code}</span>
                    <span className="min-w-0 flex-1">
                      <span className="block text-slate-700">{m.description}</span>
                      <span className="block text-xs text-slate-400">{ruleText(m.rule)}</span>
                    </span>
                    <Badge map={PROVENANCE} value={m.provenance} />
                  </li>
                ))}
              </ul>
              {release.rules?.time && (
                <p className="mt-2 text-xs text-slate-500">
                  Time: {release.rules.time.block_minutes}-minute blocks ({release.rules.time.partial_block === 'round_up' ? 'a part block counts' : 'part blocks dropped'}),{' '}
                  {release.rules.time.tiers.map((t) => `${t.units_per_block} units/block ${t.up_to_minutes == null ? 'after that' : `up to ${t.up_to_minutes} min`}`).join(', ')}.{' '}
                  {release.rules.multiple_procedures === 'highest_base_only' ? 'Only the highest-unit procedure carries base units.' : 'Every procedure carries its base units.'}
                </p>
              )}
            </>
          )}
        </section>

        <FeeSchedules schedules={schedules} setSchedules={setSchedules} />
      </div>

      <SchemeRates />
      {release && <Calculator schedules={schedules} />}
      {release && <CodeList />}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Fee schedules — rand per anaesthetic unit and per clinical unit.
// ---------------------------------------------------------------------------
const FeeSchedules = ({ schedules, setSchedules }) => {
  const [edits, setEdits] = useState({});
  const [schemes, setSchemes] = useState([]);
  const [adding, setAdding] = useState(null);
  const [busy, setBusy] = useState('');
  const [err, setErr] = useState('');

  useEffect(() => { patientService.schemes().then(setSchemes).catch(() => {}); }, []);

  const value = (s, k) => (edits[s.fee_schedule_id]?.[k] ?? s[k]);
  const setValue = (s, k, v) => setEdits((e) => ({ ...e, [s.fee_schedule_id]: { ...e[s.fee_schedule_id], [k]: v } }));
  const dirty = (s) => !!edits[s.fee_schedule_id];

  const run = async (key, fn) => {
    setBusy(key);
    setErr('');
    try {
      setSchedules(await fn());
      return true;
    } catch (e) {
      setErr(errText(e));
      return false;
    } finally {
      setBusy('');
    }
  };

  const save = async (s) => {
    const ok = await run(`save${s.fee_schedule_id}`, () => tariffService.updateSchedule(s.fee_schedule_id, {
      anaesthetic_unit_rate: value(s, 'anaesthetic_unit_rate'), clinical_unit_rate: value(s, 'clinical_unit_rate'),
    }));
    if (ok) setEdits((e) => { const n = { ...e }; delete n[s.fee_schedule_id]; return n; });
  };

  const add = async () => {
    const ok = await run('add', () => tariffService.addSchedule(adding));
    if (ok) setAdding(null);
  };

  const usedSchemes = new Set(schedules.map((s) => s.scheme_id).filter(Boolean));
  const input = 'w-24 rounded-md border border-slate-300 px-2 py-1 text-right text-sm tabular-nums focus:border-brand-teal focus:outline-none focus:ring-2 focus:ring-brand-teal/30';

  return (
    <section className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
      <div className="flex items-start gap-4">
        <span className="grid h-11 w-11 shrink-0 place-items-center rounded-xl bg-teal-50 text-brand-teal"><BanknotesIcon className="h-6 w-6" /></span>
        <div className="min-w-0 flex-1">
          <h3 className="font-bold text-slate-800">Your fee schedules</h3>
          <p className="text-sm text-slate-500">Rand per unit. A medical scheme’s own rates are used for its members; everyone else gets the practice default.</p>
        </div>
      </div>

      <div className="mt-4 overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-[11px] uppercase tracking-wide text-slate-500">
              <th className="py-1.5 font-semibold">Schedule</th>
              <th className="py-1.5 text-right font-semibold">Anaes. unit</th>
              <th className="py-1.5 text-right font-semibold">Clinical unit</th>
              <th className="w-28" />
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {schedules.map((s) => (
              <tr key={s.fee_schedule_id}>
                <td className="py-2 pr-2">
                  <div className="font-semibold text-slate-700">{s.scheme_id ? s.scheme_name : s.name}</div>
                  <div className="flex items-center gap-1.5 text-[11px] text-slate-400">
                    {s.scheme_id ? 'Scheme rates' : 'Practice default'}
                    {s.is_placeholder && <span className="rounded bg-amber-100 px-1.5 font-semibold text-amber-800">placeholder rates</span>}
                  </div>
                </td>
                <td className="py-2 text-right">R <input type="number" min="0" step="0.01" value={value(s, 'anaesthetic_unit_rate')} onChange={(e) => setValue(s, 'anaesthetic_unit_rate', e.target.value)} className={input} /></td>
                <td className="py-2 text-right">R <input type="number" min="0" step="0.01" value={value(s, 'clinical_unit_rate')} onChange={(e) => setValue(s, 'clinical_unit_rate', e.target.value)} className={input} /></td>
                <td className="py-2 pl-2 text-right">
                  {(dirty(s) || s.is_placeholder) && (
                    <button onClick={() => save(s)} disabled={!!busy} className="rounded-md bg-brand-navy px-2.5 py-1 text-xs font-semibold text-white disabled:opacity-50">
                      {busy === `save${s.fee_schedule_id}` ? 'Saving…' : s.is_placeholder && !dirty(s) ? 'Confirm' : 'Save'}
                    </button>
                  )}
                  {s.scheme_id && (
                    <button onClick={() => run(`rm${s.fee_schedule_id}`, () => tariffService.removeSchedule(s.fee_schedule_id))} disabled={!!busy} title="Remove these scheme rates" className="ml-1 rounded p-1 text-slate-300 hover:bg-red-50 hover:text-red-600">
                      <XMarkIcon className="h-4 w-4" />
                    </button>
                  )}
                </td>
              </tr>
            ))}
            {adding && (
              <tr>
                <td className="py-2 pr-2">
                  <select value={adding.scheme_id} onChange={(e) => setAdding({ ...adding, scheme_id: e.target.value })} className="w-full rounded-md border border-slate-300 px-2 py-1 text-sm">
                    <option value="">Choose a scheme…</option>
                    {schemes.filter((x) => !usedSchemes.has(x.scheme_id)).map((x) => <option key={x.scheme_id} value={x.scheme_id}>{x.name}</option>)}
                  </select>
                </td>
                <td className="py-2 text-right">R <input type="number" min="0" step="0.01" value={adding.anaesthetic_unit_rate} onChange={(e) => setAdding({ ...adding, anaesthetic_unit_rate: e.target.value })} className={input} /></td>
                <td className="py-2 text-right">R <input type="number" min="0" step="0.01" value={adding.clinical_unit_rate} onChange={(e) => setAdding({ ...adding, clinical_unit_rate: e.target.value })} className={input} /></td>
                <td className="py-2 pl-2 text-right">
                  <button onClick={add} disabled={!!busy || !adding.scheme_id} className="rounded-md bg-brand-navy px-2.5 py-1 text-xs font-semibold text-white disabled:opacity-50">{busy === 'add' ? 'Adding…' : 'Add'}</button>
                  <button onClick={() => setAdding(null)} className="ml-1 rounded p-1 text-slate-400 hover:bg-slate-100"><XMarkIcon className="h-4 w-4" /></button>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      {err && <p className="mt-2 text-sm text-red-600">{err}</p>}
      {!adding && (
        <button
          onClick={() => {
            const d = schedules.find((s) => !s.scheme_id);
            setAdding({ scheme_id: '', anaesthetic_unit_rate: d?.anaesthetic_unit_rate ?? '', clinical_unit_rate: d?.clinical_unit_rate ?? '' });
          }}
          className="mt-3 text-sm font-semibold text-brand-teal hover:underline"
        >
          + Rates for a medical scheme
        </button>
      )}
    </section>
  );
};

// ---------------------------------------------------------------------------
// Calculator — the same engine the preview invoice uses; nothing is saved.
// ---------------------------------------------------------------------------
const Calculator = ({ schedules }) => {
  const [codes, setCodes] = useState([]);
  const [facts, setFacts] = useState({ duration_minutes: '60', age_years: '', bmi: '', emergency: false, prone: false });
  const [schemeId, setSchemeId] = useState('');
  const [out, setOut] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  useEffect(() => {
    if (!codes.length) { setOut(null); return undefined; }
    let cancelled = false;
    const t = setTimeout(async () => {
      setBusy(true);
      try {
        const r = await tariffService.calculate({
          codes: codes.map((c) => c.code),
          facts: {
            duration_minutes: facts.duration_minutes === '' ? null : Number(facts.duration_minutes),
            age_years: facts.age_years === '' ? null : Number(facts.age_years),
            bmi: facts.bmi === '' ? null : Number(facts.bmi),
            emergency: facts.emergency,
            prone: facts.prone,
          },
          scheme_id: schemeId || null,
        });
        if (!cancelled) { setOut(r); setErr(''); }
      } catch (e) {
        if (!cancelled) setErr(errText(e));
      } finally {
        if (!cancelled) setBusy(false);
      }
    }, 250);
    return () => { cancelled = true; clearTimeout(t); };
  }, [codes, facts, schemeId]);

  const set = (k) => (e) => setFacts((f) => ({ ...f, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value }));
  const field = 'w-full rounded-lg border border-slate-300 px-2.5 py-2 text-sm focus:border-brand-teal focus:outline-none focus:ring-2 focus:ring-brand-teal/30';

  return (
    <section className="mb-6 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
      <div className="flex items-center gap-3 border-b border-slate-100 px-5 py-4">
        <CalculatorIcon className="h-5 w-5 text-brand-teal" />
        <div className="flex-1">
          <h3 className="font-bold text-slate-800">Fee calculator</h3>
          <p className="text-xs text-slate-500">Pick procedures and the case details to see the units, modifiers and fee. Nothing is saved.</p>
        </div>
        {out && <span className="text-lg font-bold text-brand-deep">{rands(out.total)}</span>}
      </div>

      <div className="grid gap-4 px-5 py-4 lg:grid-cols-[1fr_auto]">
        <div>
          <TariffPicker onPick={(row) => setCodes((c) => [...c, row])} exclude={codes.map((c) => c.code)} />
          <div className="mt-2 flex flex-wrap gap-1.5">
            {codes.map((c) => (
              <span key={c.code} className="inline-flex items-center gap-1 rounded-full bg-slate-100 py-0.5 pl-2.5 pr-1 text-xs text-slate-700">
                <span className="font-mono font-semibold text-brand-navy">{c.code}</span> {c.description}
                <button onClick={() => setCodes((list) => list.filter((x) => x.code !== c.code))} className="rounded-full p-0.5 hover:bg-slate-200"><XMarkIcon className="h-3.5 w-3.5" /></button>
              </span>
            ))}
            {!codes.length && <span className="text-xs text-slate-400">Try “lap chole”, “TKR” or “arterial line”.</span>}
          </div>
        </div>
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-4 lg:w-[30rem]">
          <label className="text-[11px] font-semibold text-slate-500">Minutes<input type="number" min="0" value={facts.duration_minutes} onChange={set('duration_minutes')} className={field} /></label>
          <label className="text-[11px] font-semibold text-slate-500">Age<input type="number" min="0" step="0.1" value={facts.age_years} onChange={set('age_years')} className={field} /></label>
          <label className="text-[11px] font-semibold text-slate-500">BMI<input type="number" min="10" step="0.1" value={facts.bmi} onChange={set('bmi')} className={field} /></label>
          <label className="text-[11px] font-semibold text-slate-500">Rates
            <select value={schemeId} onChange={(e) => setSchemeId(e.target.value)} className={field}>
              <option value="">Practice default</option>
              {schedules.filter((s) => s.scheme_id).map((s) => <option key={s.scheme_id} value={s.scheme_id}>{s.scheme_name}</option>)}
            </select>
          </label>
          <label className="col-span-1 flex items-center gap-2 text-sm text-slate-600"><input type="checkbox" checked={facts.emergency} onChange={set('emergency')} className="rounded border-slate-300" /> Emergency</label>
          <label className="col-span-1 flex items-center gap-2 text-sm text-slate-600"><input type="checkbox" checked={facts.prone} onChange={set('prone')} className="rounded border-slate-300" /> Prone</label>
        </div>
      </div>

      {err && <p className="px-5 pb-3 text-sm text-red-600">{err}</p>}
      {out && (
        <div className="pb-2">
          <p className="px-5 pb-2 text-xs text-slate-400">
            Priced with “{out.fee_schedule.name}”: {rands(out.rates.anaesthetic_unit_rate)} per anaesthetic unit, {rands(out.rates.clinical_unit_rate)} per clinical unit.
          </p>
          <TariffLines result={out} busy={busy} />
        </div>
      )}
    </section>
  );
};

// ---------------------------------------------------------------------------
// Code list — every code in the active release.
// ---------------------------------------------------------------------------
const CodeList = () => {
  const [open, setOpen] = useState(false);
  const [codes, setCodes] = useState(null);
  const [filter, setFilter] = useState('');

  useEffect(() => {
    if (open && codes === null) tariffService.codes().then(setCodes).catch(() => setCodes([]));
  }, [open, codes]);

  const shown = (codes || []).filter((c) => {
    const f = filter.trim().toLowerCase();
    return !f || c.code.includes(f) || `${c.description} ${c.synonyms || ''} ${c.section || ''}`.toLowerCase().includes(f);
  });

  return (
    <section className="mb-6 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
      <button onClick={() => setOpen(!open)} className="flex w-full items-center gap-3 px-5 py-4 text-left">
        <ListBulletIcon className="h-5 w-5 text-brand-teal" />
        <h3 className="flex-1 font-bold text-slate-800">All tariff codes</h3>
        <ChevronDownIcon className={`h-5 w-5 text-slate-400 transition ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <>
          <div className="border-t border-slate-100 px-5 py-3">
            <input value={filter} onChange={(e) => setFilter(e.target.value)} placeholder="Filter…" className="w-full max-w-xs rounded-lg border border-slate-300 px-3 py-1.5 text-sm" />
          </div>
          <div className="max-h-[28rem] overflow-auto">
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-slate-50">
                <tr className="text-left text-[11px] uppercase tracking-wide text-slate-500">
                  <th className="px-5 py-2 font-semibold">Code</th>
                  <th className="px-2 py-2 font-semibold">Description</th>
                  <th className="px-2 py-2 font-semibold">Section</th>
                  <th className="px-2 py-2 text-right font-semibold">Units</th>
                  <th className="px-5 py-2 font-semibold">Source</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {shown.map((c) => (
                  <tr key={c.code}>
                    <td className="px-5 py-1.5 font-mono font-semibold text-brand-navy">{c.code}</td>
                    <td className="px-2 py-1.5 text-slate-700">{c.description}{c.synonyms && <div className="text-[11px] text-slate-400">{c.synonyms}</div>}</td>
                    <td className="px-2 py-1.5 text-slate-500">{c.section}</td>
                    <td className="px-2 py-1.5 text-right tabular-nums text-slate-600">
                      {c.item_type === 'surgical' ? `${Number(c.anaesthetic_units)} anaes.` : `${Number(c.clinical_units)} clin.`}
                    </td>
                    <td className="px-5 py-1.5"><Badge map={PROVENANCE} value={c.provenance} /></td>
                  </tr>
                ))}
                {codes === null && <tr><td colSpan={5} className="px-5 py-4 text-center text-slate-400">Loading…</td></tr>}
              </tbody>
            </table>
          </div>
        </>
      )}
    </section>
  );
};

export default TariffPanel;
